import { format, isAfter } from "date-fns";
import { API_TBase } from "./API";
import { API_TUser } from "@/models/auth";

import {
  TO_DO_TASK_STATUS_LABEL,
  COMPLETED_TASK_STATUS_LABEL,
  TASK_PRIORITY_MEDIUM,
  TASK_PRIORITY_HIGH,
} from "@/constants/tasks";
import { EXPERT_ROLE, ROLE_LABELS } from "@/constants/roles";

const DATETIME_FORMAT = "dd/MM/yyyy";

export type API_TTasks = {
  employer_tasks: API_TTask[];
  candidate_tasks: API_TTask[];
};

export type API_TTask = {
  start_date: string;
  end_date: string;
  name: string;
  case_progress_step: API_TTaskStep;
  priority: string;
  status: string;
  action_by: string;
  action_by_user: API_TTaskUser;
  user: API_TTaskUser;
  user_id?: number;
} & API_TBase;

type API_TTaskUser = {
  role: string;
} & API_TBase;

type API_TTaskStep = {
  name: string;
  belongs_to_role: string;
} & API_TBase;

export type TTask_Table = {
  id: number;
  name: string;
  step: string;
  priority: string;
  startDate: string | Date;
  endDate: string | Date;
  assignedTo: string;
  actionBy: string;
  status: string;
  user?: API_TUser;
};

const replaceUnderscoreBySpace = (s: string) => s.replace("_", " ");

const firstLetterToUpperCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const convertSection = (section: string) => {
  section = replaceUnderscoreBySpace(section);
  section = firstLetterToUpperCase(section);

  return section;
};

const convertDatetime = (value: string, formatStr?: string) => {
  const datetime = new Date(value);
  const ts = datetime.getTime();

  if (ts <= 0) return "-";

  return formatStr ? format(datetime, formatStr) : datetime;
};

const getAssignedToLabel = (a) => {
  const { first_name, last_name } = a?.user; // or "a.target_user"?

  if (!first_name || !last_name) return "―";

  return `${first_name} ${last_name}`;
};

const getActionByLabel = (a) => {
  const { first_name, last_name, role } = a?.action_by_user;

  if (!first_name || !last_name) return "―";

  let name = `${first_name} ${last_name}`;

  if ([EXPERT_ROLE].includes(role)) name += ` (${ROLE_LABELS[role]})`;

  return name;
};

export const convertTaskForTable = (a: API_TTask): TTask_Table => {
  return {
    id: a.ID,
    name: a.name,
    step: convertSection(a.case_progress_step.name),
    priority: firstLetterToUpperCase(a.priority),
    assignedTo: getAssignedToLabel(a),
    actionBy: getActionByLabel(a),
    status: convertSection(a.status),
    startDate: convertDatetime(a.start_date, DATETIME_FORMAT),
    endDate: convertDatetime(a.end_date, DATETIME_FORMAT),
  };
};

type TTaskTableSort = (a: TTask_Table, b: TTask_Table) => -1 | 1 | 0;

type TTaskTableSortMap<T = TTaskTableSort> = {
  byExpDate: T;
  byPriorityDescending: T;
  byPriorityAscending: T;
  byStatusDescending: T;
  byStatusAscending: T;
};

export const tableTasksSorts: TTaskTableSortMap = {
  byExpDate: (a: TTask_Table, b: TTask_Table) => {
    return isAfter(new Date(a.endDate), new Date(b.endDate)) ? 1 : -1;
  },

  byPriorityDescending: (a: TTask_Table, b: TTask_Table) => {
    if (a.priority === TASK_PRIORITY_HIGH && b.priority === TASK_PRIORITY_MEDIUM) return -1;

    if (a.priority === TASK_PRIORITY_MEDIUM && b.priority === TASK_PRIORITY_HIGH) return 1;

    return 0;
  },

  byPriorityAscending: (a: TTask_Table, b: TTask_Table) => {
    if (a.priority === TASK_PRIORITY_MEDIUM && b.priority === TASK_PRIORITY_HIGH) return -1;

    if (a.priority === TASK_PRIORITY_HIGH && b.priority === TASK_PRIORITY_MEDIUM) return 1;

    return 0;
  },

  byStatusDescending: (a: TTask_Table, b: TTask_Table) => {
    if (a.status === TO_DO_TASK_STATUS_LABEL && b.status === COMPLETED_TASK_STATUS_LABEL) return -1;

    if (a.status === COMPLETED_TASK_STATUS_LABEL && b.status === TO_DO_TASK_STATUS_LABEL) return 1;

    return 0;
  },

  byStatusAscending: (a: TTask_Table, b: TTask_Table) => {
    if (a.status === COMPLETED_TASK_STATUS_LABEL && b.status === TO_DO_TASK_STATUS_LABEL) return -1;

    if (a.status === TO_DO_TASK_STATUS_LABEL && b.status === COMPLETED_TASK_STATUS_LABEL) return 1;

    return 0;
  },
};
